import { useEffect, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getProducts } from '../api/client'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { Zeichen } from '../components/Zeichen'
import { Pagination } from '../components/Pagination'
import { ProductGrid } from '../components/ProductGrid'
import { Fehlerhinweis, Leerhinweis, RasterSkelett } from '../components/States'
import { useResource } from '../hooks/useResource'
import { useSeo } from '../hooks/useSeo'
import { useSprache } from '../i18n'
import { useShopConfig } from '../shopConfig'

const PRO_SEITE = 24

export function SearchPage() {
  const { t, tz } = useSprache()
  const [params, setParams] = useSearchParams()
  const { shopName, primaryHost } = useShopConfig()

  const begriff = params.get('q')?.trim() ?? ''
  const seite = Math.max(1, Number(params.get('seite') ?? '1') || 1)

  const { data, error, loading } = useResource(
    (signal) => getProducts({ search: begriff, page: seite, pageSize: PRO_SEITE }, signal),
    [begriff, seite],
  )

  useSeo({
    title: begriff
      ? t('suche.seoTitel', { begriff, laden: shopName })
      : t('suche.seoTitelAlle', { laden: shopName }),
    description: begriff
      ? t('suche.seoText', { begriff, laden: shopName })
      : t('suche.seoTextAlle', { laden: shopName }),
    canonicalHost: primaryHost,
  })

  const seiten = data ? Math.max(1, Math.ceil(data.total / PRO_SEITE)) : 1

  function seiteWechseln(neu: number) {
    const naechste = new URLSearchParams(params)
    if (neu <= 1) naechste.delete('seite')
    else naechste.set('seite', String(neu))
    setParams(naechste)
    // Nach oben, sonst steht man mitten in der neuen Seite und hält sie für
    // dieselbe.
    window.scrollTo({ top: 0 })
  }

  return (
    <>
      <Breadcrumbs
        pfade={[
          { name: t('nav.startseite'), pfad: '/' },
          { name: begriff ? t('suche.krume') : t('suche.alleProdukte') },
        ]}
      />

      <header className="sf-seitenkopf">
        <h1>{begriff ? t('suche.ueberschrift', { begriff }) : t('suche.alleProdukte')}</h1>

        <Suchzeile
          begriff={begriff}
          onSuchen={(wert) => {
            // Ein neuer Begriff fängt auf Seite eins an: Seite drei von
            // „Wolle" ist bei „Socken" womöglich leer.
            const naechste = new URLSearchParams()
            if (wert) naechste.set('q', wert)
            setParams(naechste)
          }}
        />

      </header>

      {data && (
        <div className="sf-leiste">
          <span role="status">
            {data.total === 0
              ? t('suche.trefferKeine')
              : tz(data.total, 'suche.trefferEiner', 'suche.trefferViele')}
          </span>
        </div>
      )}

      {loading && <RasterSkelett />}
      {error != null && <Fehlerhinweis fehler={error} />}

      {data?.products.length === 0 && (
        <Leerhinweis
          text={begriff ? t('suche.nichtsGefunden', { begriff }) : t('produkt.keine')}
        />
      )}

      {data && data.products.length > 0 && (
        <>
          <ProductGrid produkte={data.products} />
          <Pagination seite={seite} seiten={seiten} onWechsel={seiteWechseln} />
        </>
      )}
    </>
  )
}

/**
 * Das Suchfeld auf der Trefferseite selbst.
 *
 * Wer „Wolle" gesucht hat und nichts fand, will „Woll" versuchen — ohne dafür
 * oben das Fenster zu öffnen. Das Feld hält seinen eigenen Text, bis abgeschickt
 * wird: Die Adresse ändert sich erst dann, und die Liste lädt nicht bei jedem
 * Buchstaben neu.
 *
 * Ein eigenes <form> — und das einzige auf dieser Seite. Ein Formular in einem
 * Formular wirft der Browser weg, und dann löste dieser Knopf aus, was gerade
 * außen herum liegt.
 */
function Suchzeile({ begriff, onSuchen }: { begriff: string; onSuchen: (wert: string) => void }) {
  const { t } = useSprache()
  const [wert, setWert] = useState(begriff)

  // Kommt ein anderer Begriff aus der Adresse (Zurück-Knopf, Suchfenster in
  // der Kopfzeile), zieht das Feld nach — sonst stünde im Feld „Wolle" über
  // einer Liste zu „Socken".
  useEffect(() => setWert(begriff), [begriff])

  function abschicken(ereignis: FormEvent) {
    ereignis.preventDefault()
    onSuchen(wert.trim())
  }

  return (
    <form className="sf-suchfeld" role="search" onSubmit={abschicken}>
      <Zeichen art="suche" groesse={20} />
      <label className="sf-bildschirmleser" htmlFor="suchzeile-feld">
        {t('suche.feldLabel')}
      </label>
      <input
        id="suchzeile-feld"
        type="search"
        name="q"
        placeholder={t('suche.platzhalter')}
        value={wert}
        onChange={(ereignis) => setWert(ereignis.target.value)}
      />
      <button className="sf-knopf sf-knopf-haupt" type="submit" style={{ minHeight: '2.6rem' }}>
        {t('suche.knopf')}
      </button>
    </form>
  )
}
